import { Box, Button, Heading, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Answer, Question } from '../types/Questionnaire'
import QuestionCarouselNavigation from './QuestionCarouselNavigation'

interface QuestionnaireCarouselProps {
  answers: Record<string, Answer>
  questionIds: string[]
  questions: Record<string, Question>
  onChange: (questionId: string, answer: Answer) => void
}

const selectedAnswers = (
  answers: Record<string, Answer>,
  key: keyof Answer,
) =>
  Object.entries(answers).reduce<Record<string, number>>(
    (selected, [questionId, answer]) => {
      const value = answer[key]
      if (value !== undefined) selected[questionId] = value
      return selected
    },
    {},
  )

export default function QuestionnaireCarousel({
  answers,
  questionIds,
  questions,
  onChange,
}: QuestionnaireCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedOptions, setSelectedOptions] = useState(() =>
    selectedAnswers(answers, 'option'),
  )
  const [selectedSets, setSelectedSets] = useState(() =>
    selectedAnswers(answers, 'set'),
  )

  useEffect(() => {
    setSelectedOptions(selectedAnswers(answers, 'option'))
    setSelectedSets(selectedAnswers(answers, 'set'))
  }, [answers])

  const questionId = questionIds[currentIndex]
  const question = questions[questionId]
  const selectedSet = selectedSets[questionId]
  const optionSet = question.switch ? selectedSet ?? 0 : 0

  const handleOptionChange = (option: number) => {
    setSelectedOptions((options) => ({ ...options, [questionId]: option }))
    onChange(questionId, { option, ...(selectedSet !== undefined && { set: selectedSet }) })
    if (currentIndex < questionIds.length - 1) {
      setCurrentIndex((index) => index + 1)
    }
  }

  const handleSwitchChange = (set: number) => {
    setSelectedSets((sets) => ({ ...sets, [questionId]: set }))
    setSelectedOptions((options) => {
      const { [questionId]: _previousOption, ...remainingOptions } = options
      return remainingOptions
    })
    onChange(questionId, { set })
  }

  return (
    <Stack spacing={4}>
      <Box bg='purple.50' borderColor='purple.100' borderRadius='lg' borderWidth='1px' p={6}>
        <VStack align='stretch' spacing={4}>
          <Text color='gray.500' fontSize='lg' textAlign='center'>
            {currentIndex + 1} of {questionIds.length}
          </Text>
          {question.switch && (
            <Stack spacing={2}>
              <Heading
                fontSize='xl'
                overflowWrap='anywhere'
                textAlign='center'
                whiteSpace='normal'
              >
                {currentIndex + 1}. {question.switch}
              </Heading>
              <HStack justify='center'>
                {['Yes', 'No'].map((label, set) => (
                  <Button
                    colorScheme='purple'
                    key={label}
                    onClick={() => handleSwitchChange(set)}
                    aria-pressed={selectedSet === set}
                    variant={selectedSet === set ? 'solid' : 'outline'}
                  >
                    {label}
                  </Button>
                ))}
              </HStack>
            </Stack>
          )}
          {(!question.switch || selectedSet !== undefined) && (
            <Stack spacing={2}>
              <Heading
                fontSize='xl'
                overflowWrap='anywhere'
                textAlign='center'
                whiteSpace='normal'
              >
                {currentIndex + 1}
                {question.switch ? (optionSet === 0 ? 'a' : 'b') : ''}. {question.title}
              </Heading>
              {question.options[optionSet].map((option, index) => (
                <Button
                  colorScheme='purple'
                  key={option.text}
                  onClick={() => handleOptionChange(index)}
                  height='auto'
                  minH='3rem'
                  overflowWrap='anywhere'
                  size='lg'
                  whiteSpace='normal'
                  aria-pressed={selectedOptions[questionId] === index}
                  variant={selectedOptions[questionId] === index ? 'solid' : 'outline'}
                >
                  {option.text}
                </Button>
              ))}
            </Stack>
          )}
          <QuestionCarouselNavigation
            isNextDisabled={currentIndex === questionIds.length - 1}
            isPreviousDisabled={currentIndex === 0}
            onNext={() =>
              setCurrentIndex((index) => Math.min(questionIds.length - 1, index + 1))
            }
            onPrevious={() => setCurrentIndex((index) => Math.max(0, index - 1))}
          />
        </VStack>
      </Box>
    </Stack>
  )
}
